import { useState, useEffect, useCallback } from 'react';
import { useGetAccessTokens, useCreateAccessToken, useRevokeAccessToken, customInstance, type AccessToken } from '@edison/api-client-react';
import { Key, Plus, XCircle, AlertCircle, Copy, CheckCircle2 } from 'lucide-react';
import { Button } from '@/components/ui/button.js';
import { Badge } from '@/components/ui/badge.js';

interface Props {
  claimId: string;
  claimStatus: string;
}

const ROLE_LABEL: Record<string, string> = {
  auditor: 'Auditor',
  advisor: 'Advisor',
  due_diligence: 'Due Diligence',
};

function formatExpiry(iso: string) {
  return new Date(iso).toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' });
}

function TokenRow({
  token,
  onRevoke,
  isRevoking,
}: {
  token: AccessToken;
  onRevoke: (t: AccessToken) => void;
  isRevoking: boolean;
}) {
  const [copied, setCopied] = useState(false);
  const isRevoked = !!token.revokedAt;
  const isExpired = new Date(token.expiresAt) < new Date();

  return (
    <div className="flex items-center justify-between gap-4 rounded-lg border p-3">
      <div className="space-y-1">
        <div className="flex items-center gap-2">
          <p className="text-sm font-medium">{token.label ?? ROLE_LABEL[token.role] ?? token.role}</p>
          <Badge variant="info">{ROLE_LABEL[token.role] ?? token.role}</Badge>
          {(isRevoked || isExpired) && (
            <Badge variant="default">{isRevoked ? 'Revoked' : 'Expired'}</Badge>
          )}
        </div>
        <p className="text-xs text-muted-foreground">
          expires {formatExpiry(token.expiresAt)}
        </p>
        {(() => {
          // lastUsedAt/lastUsedIp are returned by the API but not in the generated type.
          const usage = token as AccessToken & { lastUsedAt?: string | null; lastUsedIp?: string | null };
          return (
            <p className="text-xs text-muted-foreground">
              {usage.lastUsedAt
                ? `last used ${new Date(usage.lastUsedAt).toLocaleString('en-AU')}${usage.lastUsedIp ? ` from ${usage.lastUsedIp}` : ''}`
                : 'never used'}
            </p>
          );
        })()}
        {/* Raw token displayed only on create — it will be in token.token field */}
        {token.token && (
          <div className="mt-1 flex items-center gap-2">
            <code className="text-xs bg-muted px-2 py-0.5 rounded font-mono truncate max-w-xs">
              {token.token}
            </code>
            <button
              onClick={() => {
                void navigator.clipboard.writeText(token.token!);
                setCopied(true);
                setTimeout(() => setCopied(false), 2000);
              }}
              className="text-muted-foreground hover:text-foreground"
              aria-label="Copy token"
            >
              {copied ? (
                <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" aria-hidden="true" />
              ) : (
                <Copy className="h-3.5 w-3.5" aria-hidden="true" />
              )}
            </button>
            <span className="text-xs text-amber-600 font-medium">Copy now — not shown again</span>
          </div>
        )}
      </div>
      {!isRevoked && !isExpired && (
        <Button
          variant="destructive"
          size="sm"
          onClick={() => onRevoke(token)}
          disabled={isRevoking}
        >
          <XCircle className="h-3.5 w-3.5" aria-hidden="true" />
          Revoke
        </Button>
      )}
    </div>
  );
}

export function AccessTokensPanel({ claimId, claimStatus }: Props) {
  const { data, isLoading, error, refetch } = useGetAccessTokens(claimId);
  const { mutate: createToken, isPending: isCreating } = useCreateAccessToken();
  const { mutate: revokeToken, isPending: isRevoking } = useRevokeAccessToken();

  // Per-role default + max expiry (must match ROLE_MAX_DAYS on the server).
  const ROLE_EXPIRY: Record<'auditor' | 'advisor' | 'due_diligence', { def: number; max: number }> = {
    auditor: { def: 30, max: 90 },
    advisor: { def: 30, max: 60 },
    due_diligence: { def: 14, max: 30 },
  };

  const [showForm, setShowForm] = useState(false);
  const [newToken, setNewToken] = useState<AccessToken | null>(null);
  const [formRole, setFormRole] = useState<'auditor' | 'advisor' | 'due_diligence'>('auditor');
  const [formLabel, setFormLabel] = useState('');
  const [formDays, setFormDays] = useState(ROLE_EXPIRY.auditor.def);

  // When the role changes, snap the expiry to that role's default and clamp to its max.
  const onRoleChange = (role: 'auditor' | 'advisor' | 'due_diligence') => {
    setFormRole(role);
    setFormDays(ROLE_EXPIRY[role].def);
  };
  const roleMaxDays = ROLE_EXPIRY[formRole].max;

  const tokens = data?.data?.items ?? [];
  const canCreate = claimStatus === 'locked';

  const handleCreate = () => {
    const expiresAt = new Date(Date.now() + formDays * 24 * 60 * 60 * 1000).toISOString();
    createToken(
      { claimId, role: formRole, label: formLabel || undefined, expiresAt },
      {
        onSuccess: (res) => {
          setNewToken(res.data);
          setShowForm(false);
          setFormLabel('');
          void refetch();
        },
      }
    );
  };

  const handleRevoke = (token: AccessToken) => {
    revokeToken({ id: token.id, claimId }, { onSuccess: () => void refetch() });
  };

  // Pending renewal requests (endpoints are outside the generated client, called via customInstance).
  interface RenewalRequest { id: string; accessTokenId: string; note: string | null; createdAt: string; }
  const [renewals, setRenewals] = useState<RenewalRequest[]>([]);
  const loadRenewals = useCallback(async () => {
    try {
      const res = await customInstance<{ data: { items: RenewalRequest[] } }>({
        url: '/api/access-tokens/renewal-requests', method: 'GET', params: { claimId },
      });
      setRenewals(res.data.items);
    } catch { /* non-fatal */ }
  }, [claimId]);
  useEffect(() => { void loadRenewals(); }, [loadRenewals]);

  const handleApproveRenewal = async (id: string) => {
    const res = await customInstance<{ data: AccessToken }>({ url: `/api/access-tokens/renewal-requests/${id}/approve`, method: 'POST' });
    setNewToken(res.data); // reveal the reissued raw token once
    await loadRenewals();
    void refetch();
  };
  const handleRejectRenewal = async (id: string) => {
    await customInstance({ url: `/api/access-tokens/renewal-requests/${id}/reject`, method: 'POST' });
    await loadRenewals();
  };

  if (isLoading) {
    return <div className="h-24 w-full rounded-lg skeleton" />;
  }

  return (
    <section className="rounded-lg border p-5 space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Key className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
          <h2 className="font-medium text-sm">Third-party access tokens</h2>
        </div>
        {canCreate && (
          <Button
            variant="secondary"
            size="sm"
            onClick={() => { setShowForm(true); setNewToken(null); }}
          >
            <Plus className="h-3.5 w-3.5" aria-hidden="true" />
            Issue token
          </Button>
        )}
      </div>

      {!canCreate && (
        <p className="text-xs text-muted-foreground">
          Access tokens can only be issued once the claim is locked.
        </p>
      )}

      {renewals.length > 0 && (
        <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 space-y-2">
          <p className="text-xs font-medium text-amber-900">
            {renewals.length} renewal request{renewals.length > 1 ? 's' : ''} awaiting your review
          </p>
          {renewals.map((r) => (
            <div key={r.id} className="flex items-center justify-between gap-3 rounded border bg-background p-2">
              <div className="min-w-0">
                <p className="text-xs text-muted-foreground truncate">
                  {r.note ? `“${r.note}”` : 'No note provided'}
                </p>
                <p className="text-[11px] text-muted-foreground">requested {new Date(r.createdAt).toLocaleString('en-AU')}</p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <Button variant="secondary" size="sm" onClick={() => void handleApproveRenewal(r.id)}>
                  Approve &amp; reissue
                </Button>
                <Button variant="ghost" size="sm" onClick={() => void handleRejectRenewal(r.id)}>
                  Reject
                </Button>
              </div>
            </div>
          ))}
          <p className="text-[11px] text-amber-800">
            Approving mints a new token (shown once) and revokes the old one — send the new link to the auditor.
          </p>
        </div>
      )}

      {error && (
        <div className="flex items-center gap-2 text-destructive text-xs">
          <AlertCircle className="h-3.5 w-3.5" aria-hidden="true" />
          Failed to load tokens.
        </div>
      )}

      {/* New token banner */}
      {newToken && (
        <div className="rounded-lg border border-amber-300 bg-amber-50 p-3">
          <p className="text-xs font-medium text-amber-800 mb-1">Token created — copy it now. It will not be shown again.</p>
          <TokenRow token={newToken} onRevoke={handleRevoke} isRevoking={isRevoking} />
        </div>
      )}

      {/* Issue form */}
      {showForm && (
        <div className="rounded-lg border p-4 space-y-3">
          <div className="space-y-2">
            <label className="block text-xs font-medium" htmlFor="token-role">Role</label>
            <select
              id="token-role"
              value={formRole}
              onChange={(e) => onRoleChange(e.target.value as typeof formRole)}
              className="w-full rounded border px-2 py-2 text-sm"
            >
              <option value="auditor">Auditor</option>
              <option value="advisor">Advisor</option>
              <option value="due_diligence">Due Diligence</option>
            </select>
            <p className="text-xs text-muted-foreground">
              Only <strong>Auditor</strong> can view the underlying evidence (commits, transcripts).
              Advisor and Due Diligence see the claim summary, expenditure breakdown and eligibility only.
            </p>
          </div>
          <div className="space-y-2">
            <label className="block text-xs font-medium" htmlFor="token-label">Label (optional)</label>
            <input
              id="token-label"
              type="text"
              value={formLabel}
              onChange={(e) => setFormLabel(e.target.value)}
              placeholder="e.g. BDO Audit 2025"
              className="w-full rounded border px-2 py-2 text-sm"
            />
          </div>
          <div className="space-y-2">
            <label className="block text-xs font-medium" htmlFor="token-days">Expiry (days, max {roleMaxDays})</label>
            <input
              id="token-days"
              type="number"
              min={1}
              max={roleMaxDays}
              value={formDays}
              onChange={(e) => setFormDays(Math.min(roleMaxDays, parseInt(e.target.value) || 1))}
              className="w-24 rounded border px-2 py-2 text-sm"
            />
          </div>
          <div className="flex gap-2">
            <Button variant="primary" size="sm" onClick={handleCreate} disabled={isCreating}>
              {isCreating ? 'Issuing…' : 'Issue token'}
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setShowForm(false)}>
              Cancel
            </Button>
          </div>
        </div>
      )}

      {/* Token list */}
      {tokens.length > 0 && (
        <div className="space-y-2">
          {tokens.map((t) => (
            <TokenRow key={t.id} token={t} onRevoke={handleRevoke} isRevoking={isRevoking} />
          ))}
        </div>
      )}
      {tokens.length === 0 && !showForm && !newToken && canCreate && (
        <p className="text-sm text-muted-foreground text-center py-4">No tokens issued yet.</p>
      )}
    </section>
  );
}
